import { AuditLogDto } from '_lib/api';
import { useLocalization } from '@fluent/react';
import { CircularProgress, Typography, useTheme } from '@material-ui/core';
import React, { FC } from 'react';

import styles from './styles';
import { actionColor, actionLabelKey, auditActor, metadataSummary } from './utils';

export interface AuditHistoryListProps {
  entries: AuditLogDto[];
  total: number;
  loading: boolean;
  error: string | null;
}

const AuditHistoryList: FC<AuditHistoryListProps> = ({ entries, total, loading, error }) => {
  const classes = styles();
  const theme = useTheme();
  const { l10n } = useLocalization();

  if (loading && entries.length === 0) {
    return (
      <div className={classes.spinner}>
        <CircularProgress size={20} />
      </div>
    );
  }

  if (error && entries.length === 0) {
    return (
      <Typography variant="caption" className={classes.statusError}>
        {l10n.getString('audit-error')}
      </Typography>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography variant="caption" className={classes.status}>
        {l10n.getString('audit-empty')}
      </Typography>
    );
  }

  return (
    <div className={classes.list}>
      {entries.map((entry) => {
        const meta = metadataSummary(entry);
        const color = actionColor(theme, entry.action);
        return (
          <div key={entry.id} className={classes.entry} style={{ borderLeftColor: color }}>
            <div className={classes.entryTop}>
              <Typography variant="caption" className={classes.action} style={{ color }}>
                {l10n.getString(actionLabelKey(entry.action))}
              </Typography>
              <Typography variant="caption" className={classes.when}>
                {new Date(entry.createdAt).toLocaleString()}
              </Typography>
            </div>

            <Typography variant="body2" className={classes.who}>
              {auditActor(entry) ?? l10n.getString('audit-user-unknown')}
            </Typography>

            {meta ? (
              <Typography variant="caption" className={classes.meta}>
                {l10n.getString(meta.id, meta.args)}
              </Typography>
            ) : null}
          </div>
        );
      })}

      {total > entries.length ? (
        <Typography variant="caption" className={classes.note}>
          {l10n.getString('audit-showing', { shown: entries.length, total })}
        </Typography>
      ) : null}
    </div>
  );
};

export default React.memo(AuditHistoryList);
